import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { Form } from "antd";

import VLWizard from "views/uikit/VLWizard";
import { WizardStep } from "views/uikit/VLWizard";
import AddDataContent from "./Steps/AddDataContent";
import SelectEnrichmentModel from "./Steps/SelectEnrichmentModel";
import styles from "./style.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { getCreationStatus } from "redux/CreateDataset/selectors";
import { useNavigate, useParams } from "react-router";
import CreateDataset from "views/pages/DatasetCreationPage/DatasetCreationWizard/Steps/CreateDataset";
import {
  startCreationStatusPolling,
  stopCreationStatusPolling,
} from "redux/CreateDataset/actions";
import { useV3DatasetCreation } from "./useV3DataCreation";
import { toast } from "react-toastify";
import VLToastComponent from "views/components/VLToastComponent";
import classNames from "classnames";
import { useEventLogsPolling } from "./useEventLogsPolling";
import { DatasetStatus } from "../../../../types";
import { useFeatureFlagService } from "../../../../hooks/useFeatureFlagService";
import { USAGE_REPORT } from "../../../../helpers/constants/filters";

const getStepStatus = (currentStep: number, step: number) => {
  if (currentStep === step) return "process";
  if (currentStep > step) return "finish";
  return "wait";
};

interface OnPremDatasetCreationProps {
  batchId?: string;
}

const OnPremDatasetCreation: React.FC<OnPremDatasetCreationProps> = ({
  batchId,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const creationStatus = useSelector(getCreationStatus);
  const { newDatasetId: enrichDatasetId } = useParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const toastIdRef = useRef<any>(null);
  const pathValue = Form.useWatch("path", form);

  const {
    modelList = [],
    isModelListLoading,
    selectedModelIds,
    fetchEnrichmentModels,
    handleModelSelection,
    handleDeclineConfirmation,
    handleAcceptOverrideConfirmation,
    selectionAlertModel,
    modelDependencyMessages,
    disabledModelIds,
    triggerEnrichmentPipeline,
  } = useV3DatasetCreation(form, batchId);

  const isUsageReportEnabled = useFeatureFlagService(USAGE_REPORT).enabled;

  const {
    startContinuousEventLogsPolling,
    stopEventLogsPolling,
    fetchEventLogsOnce,
  } = useEventLogsPolling(
    enrichDatasetId,
    () => creationStatus?.status === DatasetStatus.ERROR,
    isUsageReportEnabled,
  );

  useEffect(() => {
    fetchEventLogsOnce();
  }, [fetchEventLogsOnce]);

  const onPremiseCreationClassnames = classNames(styles.onPremiseCreation, {
    [styles.showPointerOnStep]: currentStep === 1,
  });

  const progress = useMemo(() => {
    if (!creationStatus?.secondaryStatus.length) return 0;
    const total = creationStatus.secondaryStatus.reduce(
      (sum, item) => sum + item.progress,
      0,
    );
    return Math.round(total / creationStatus.secondaryStatus.length);
  }, [creationStatus]);

  const stopEventLogPolling = useCallback(() => {
    if (progress === 100 && creationStatus?.status === "READY") {
      stopEventLogsPolling();
      fetchEventLogsOnce(); //make sure to get latest status
    }
  }, [progress, creationStatus, stopEventLogsPolling, fetchEventLogsOnce]);

  useEffect(() => {
    if (progress === 100) {
      dispatch(stopCreationStatusPolling() as any);
      stopEventLogPolling();
    }
  }, [progress, dispatch, stopEventLogPolling]);

  const showErrorToast = (title: string, description: string) => {
    toastIdRef.current = toast(
      <VLToastComponent
        title={title}
        status="ERROR"
        description={description}
        onClose={() => toast.dismiss(toastIdRef.current)}
      />,
      { autoClose: 7000, closeButton: false, closeOnClick: false },
    );
  };

  const handleNext = useCallback(async () => {
    if (currentStep === 0) {
      try {
        await form.validateFields(["sourceType", "path"]);
      } catch {
        return;
      }
    } else if (currentStep === 1) {
      const result = await triggerEnrichmentPipeline();
      if (result?.success) {
        dispatch(startCreationStatusPolling(enrichDatasetId as any) as any);
      } else {
        showErrorToast(
          "Failed to trigger enrichment pipeline",
          result?.error ?? "",
        );
        return;
      }
      startContinuousEventLogsPolling();
    } else if (currentStep === 2) {
      navigate(`/dataset/${enrichDatasetId!}/data`);
    }
    setCurrentStep(currentStep + 1);
  }, [
    currentStep,
    dispatch,
    enrichDatasetId,
    navigate,
    form,
    triggerEnrichmentPipeline,
    startContinuousEventLogsPolling,
  ]);

  const handlePrev = useCallback(async () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const showBackButton = (currentStep: number) =>
    currentStep === 0 ? false : currentStep < 2;

  const goBackOnPreviousStep = useCallback(() => {
    if (currentStep === 1) {
      setCurrentStep(0);
    }
  }, [currentStep, setCurrentStep]);

  const buttonsConfig = useMemo(() => {
    const getNextButtonTooltip = () => {
      if (currentStep === 0 && !pathValue) {
        return "Please enter a path to continue";
      }
      if (currentStep === 1 && isModelListLoading) {
        return "Please wait for the models to be loaded";
      }
      if (currentStep === 2 && progress < 100) {
        return "Dataset creation in progress";
      }
    };

    return {
      showNext: true,
      showPrev: showBackButton(currentStep),
      nextText: currentStep === 2 ? "Finish" : "Continue",
      prevText: "Back",
      onNext: handleNext,
      onPrev: handlePrev,
      disableNextTooltip: getNextButtonTooltip() ?? undefined,
    };
  }, [
    handleNext,
    handlePrev,
    currentStep,
    progress,
    isModelListLoading,
    pathValue,
  ]);

  const steps: WizardStep[] = useMemo(
    () => [
      {
        title: "Add Data",
        status: getStepStatus(currentStep, 0),
        disabled: false,
        content: (
          <Form
            form={form}
            initialValues={{ sourceType: "folder" }}
            onFinish={handleNext}
          >
            <AddDataContent />
          </Form>
        ),
        onClick: goBackOnPreviousStep,
      },
      {
        title: "Enrich your data",
        status: getStepStatus(currentStep, 1),
        disabled: false,
        content: (
          <Form form={form}>
            <SelectEnrichmentModel
              modelList={modelList}
              selectedModelIds={selectedModelIds}
              isModelListLoading={isModelListLoading}
              handleModelSelection={handleModelSelection}
              fetchEnrichmentModels={fetchEnrichmentModels}
              handleDeclineConfirmation={handleDeclineConfirmation}
              handleAcceptOverrideConfirmation={
                handleAcceptOverrideConfirmation
              }
              selectionAlertModel={selectionAlertModel}
              modelDependencyMessages={modelDependencyMessages}
              disabledModelIds={disabledModelIds}
            />
          </Form>
        ),
      },
      {
        title: "Create dataset",
        status: getStepStatus(currentStep, 2),
        disabled: false,
        content: <CreateDataset />,
      },
    ],
    [
      currentStep,
      modelList,
      selectedModelIds,
      isModelListLoading,
      handleModelSelection,
      goBackOnPreviousStep,
      fetchEnrichmentModels,
      handleDeclineConfirmation,
      handleAcceptOverrideConfirmation,
      selectionAlertModel,
      disabledModelIds,
      modelDependencyMessages,
      form,
      handleNext,
    ],
  );

  return (
    <div className={onPremiseCreationClassnames}>
      <VLWizard
        steps={steps}
        current={currentStep}
        buttonsConfig={buttonsConfig}
        showActivityLogs={true}
      />
    </div>
  );
};

export default OnPremDatasetCreation;
